import { useEffect, useMemo, useState } from 'react';
import { api, type ProfitIdleRow } from '../lib/api';
import { ErrorBox, money } from '../components/DataTable';
import { useProfitMonths } from './profitMonths';
import { CalcNote } from '../components/CalcNote';

/**
 * 경영 분석 › 일 없는 기사 — 시트 「👨‍✈️ 기사LIST」(매출없는 기사)·「45인승」·「25인승」 대체(10-24).
 * 서버 `listProfitIdle`(`functions/src/profitOps.ts`) — 그 달 기사 정산현황(차주 축) 중 정산 체크 · 운행료 0원 · 차인지급액 100만 원 미만.
 * 연락처·주소는 싣지 않는다(기준정보에서).
 */

type Seat = 'all' | 'big' | 'small';
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function ProfitIdle() {
  const [seat, setSeat] = useState<Seat>('all');
  const [data, setData] = useState<{ rows: ProfitIdleRow[]; checked: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [months, mon, setMon] = useProfitMonths(setError);

  useEffect(() => {
    if (!mon) return;
    let cancelled = false;
    setData(null); setError(null);
    api.listProfitIdle({ mon }).then((r) => { if (!cancelled) setData(r); }).catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [mon]);

  // 시트 「45인승」 = 인승 28 넘음 · 「25인승」 = 28 미만
  const shown = useMemo(() => (data?.rows ?? []).filter((r) => seat === 'all'
    || (seat === 'big' ? (r.full_cnt ?? 0) > 28 : r.full_cnt !== null && r.full_cnt < 28)), [data, seat]);
  const n = (s: Seat) => (data?.rows ?? []).filter((r) => s === 'big' ? (r.full_cnt ?? 0) > 28 : r.full_cnt !== null && r.full_cnt < 28).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-auto" value={mon} onChange={(e) => setMon(e.target.value)} aria-label="정산월">
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="flex gap-1 text-sm">
          {([['all', `전체 ${data?.rows.length ?? 0}`], ['big', `45인승 ${n('big')}`], ['small', `25인승 ${n('small')}`]] as const).map(([k, l]) => (
            <button key={k} className={seat === k ? 'btn-primary' : 'btn'} onClick={() => setSeat(k)}>{l}</button>
          ))}
        </div>
        <CalcNote>기사 정산현황(차주)에서 정산 체크된 사람 중 그 달 운행료가 0원이고 차인지급액이 100만 원 미만인 차주입니다 — 회사·회계 계정(윤일향·더모아·동영·뉴동영관광·회계계정)은 뺐습니다.
        소속·차량은 그 달에 걸친 편성 중 가장 늦게 시작한 것입니다.</CalcNote>
        <span className="ml-auto text-xs text-ink-soft">운행료 0원 · 차인지급액 100만 원 미만{data ? ` · 정산 체크 ${data.checked}명 중` : ''}</span>
      </div>
      {error && <ErrorBox message={error} />}
      {!data && !error && <div className="py-8 text-center text-sm text-ink-soft">세는 중…</div>}
      {data && (
        <div className="card max-h-[calc(100vh-12rem)] overflow-auto">
          <table className="w-full min-w-[48rem] text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                {['차주', '차주 ID', '소속', '차량', '인승', '연식'].map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}
                <th className="px-2 py-1.5 text-right">영업계</th><th className="px-2 py-1.5 text-right">재무계</th><th className="px-2 py-1.5 text-right">차인지급액</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.owner_id} className="border-b border-line/60 hover:bg-surface-soft">
                  <td className="px-2 py-1">{r.name}</td>
                  <td className="px-2 py-1 text-ink-soft">{r.owner_id}</td>
                  <td className="px-2 py-1 text-ink-soft">{r.grp_name ?? '—'}</td>
                  <td className="whitespace-nowrap px-2 py-1">{r.car_no ?? '—'}</td>
                  <td className="px-2 py-1">{r.full_cnt ?? '—'}</td>
                  <td className="px-2 py-1 text-ink-soft">{r.car_year ?? '—'}</td>
                  <td className="px-2 py-1 text-right">{money(r.sales_total)}</td>
                  <td className="px-2 py-1 text-right">{money(r.fin_total)}</td>
                  <td className={`px-2 py-1 text-right font-medium ${r.grand_total < 0 ? 'text-red-700' : ''}`}>{money(r.grand_total)}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-ink-soft">해당하는 기사가 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
